import { useGetAccountQuery } from '@/services/accounts';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { Alert, Box, CheckboxInput } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  AccountDocument,
  HVACDocumentTypes,
  HVACInstallPackageFileCategory,
  ProgramType,
  QueueStatus,
  QueueType,
} from 'types';

import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import FormSection from '../Forms/FormSection';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';

import { WarrantyRegistrationReview } from './WarrantyRegistrationReview';

const oemWarrantyDocumentTypes = [
  HVACDocumentTypes.oemWarranty,
  HVACInstallPackageFileCategory.oemWarrantyDocuments,
];

// Accounts enrolled before the registration writers stamped a submission date have no value here.
// `new Date(undefined ?? '')` renders the literal string "Invalid Date", which reads as corruption
// rather than an absent field, so fall back to an explicit placeholder instead.
const MISSING_SUBMISSION_DATE = 'Unknown';

const formatSubmissionDate = (submissionDate: Date | string | undefined): string => {
  if (!submissionDate) return MISSING_SUBMISSION_DATE;
  const parsed = new Date(submissionDate);
  return Number.isNaN(parsed.getTime()) ? MISSING_SUBMISSION_DATE : parsed.toLocaleDateString();
};

const Warranties = () => {
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const { id = '' } = useParams<{ id: string }>();
  const { data: warrantyRegistrationQueue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: QueueType.warrantyRegistration,
  });
  const { data: accountResponse } = useGetAccountQuery(id);

  const account: AccountDocument<ProgramType.hvac | ProgramType.waterHeater> | undefined = accountResponse;

  const isPendingQueue = warrantyRegistrationQueue && warrantyRegistrationQueue.status !== QueueStatus.approved;
  const showQueueBanner = isFinCoUser && isPendingQueue;

  const [isOemCheckboxChecked, setIsOemCheckboxChecked] = useState(false);
  const [isExtendedCheckboxChecked, setIsExtendedCheckboxChecked] = useState(
    Boolean(account?.warrantyContracts?.provider),
  );

  const { data: oemWarrantyDocs = [] } = useGetDocumentsQuery({
    accountId: id,
    documentTypeFilter: oemWarrantyDocumentTypes,
  });
  const { data: extendedWarrantyDocs = [] } = useGetDocumentsQuery({
    accountId: id,
    documentTypeFilter: [HVACDocumentTypes.extendedWarranty],
  });

  const isOemWarrantyFileUploaded = oemWarrantyDocs.length > 0;
  const isExtendedWarrantyFileUploaded = extendedWarrantyDocs.length > 0;
  const canApproveQueue = isOemCheckboxChecked && isExtendedCheckboxChecked;

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      switch (event.target.id) {
        case 'oemWarrantyRegistered':
          setIsOemCheckboxChecked(event.target.checked);
          break;
        case 'extendedWarrantyRegistered':
          setIsExtendedCheckboxChecked(event.target.checked);
          break;
        default:
          break;
      }
    },
    [setIsOemCheckboxChecked, setIsExtendedCheckboxChecked],
  );

  return (
    <>
      {showQueueBanner && <WarrantyRegistrationReview disableApproval={!canApproveQueue} />}
      <FormSection title="OEM Warranties" description="Submit proof of warranty registration with manufacturer(s)">
        {isPendingQueue && (
          <CheckboxInput
            label="I have registered the system with the original equipment manufacturer(s)"
            name="warrantyRegistered"
            isDisabled={!isOemWarrantyFileUploaded}
            id="oemWarrantyRegistered"
            isChecked={isOemCheckboxChecked}
            onChange={handleCheckboxChange}
          />
        )}
        <DocumentList
          documentTypeFilter={oemWarrantyDocumentTypes}
          title="OEM Warranty Documents"
          showTypeLabelOnItems={false}
          showDocumentActions={isFinCoUser}
          showUploadButton={isFinCoUser}
          baseCategory={HVACDocumentTypes.oemWarranty}
        />
      </FormSection>
      <FormSection title="Extended Warranties" description="Submit proof of extended warranty registration">
        {account?.warrantyContracts && (
          <Alert
            variant="info"
            title={`${account?.warrantyContracts?.provider} Warranty Submitted`}
            message={
              <Box gap="sm">
                <Box>Submitted on: {formatSubmissionDate(account?.warrantyContracts?.submissionDate)}</Box>
                <Box>
                  {account?.warrantyContracts?.contracts.map((contract, index) => (
                    <Box key={index} gap="xs">
                      <Box>Contract Id: {contract.contractId} </Box>
                      <Box>Dealer Invoice Number: {contract.dealerInvoiceNumber}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            }
          />
        )}
        {isPendingQueue && (
          <CheckboxInput
            label="I have registered the system with the extended warranty provider(s)"
            name="warrantyRegistered"
            isDisabled={!isExtendedWarrantyFileUploaded}
            id="extendedWarrantyRegistered"
            isChecked={isExtendedCheckboxChecked}
            onChange={handleCheckboxChange}
          />
        )}
        <DocumentList
          documentTypeFilter={[HVACDocumentTypes.extendedWarranty]}
          title="Extended Warranty Documents"
          showTypeLabelOnItems={false}
          showDocumentActions={isFinCoUser}
          showUploadButton={isFinCoUser}
          baseCategory={HVACDocumentTypes.extendedWarranty}
        />
      </FormSection>
    </>
  );
};

export default Warranties;
